import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Grid,
  Paper
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  Notifications,
  Send,
  People,
  Person,
  CheckCircle
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { notificacaoService, usuarioService } from '../../services/api';
import { useLocation } from 'react-router-dom';

interface Usuario {
  id: number;
  user_name: string;
  nome: string;
  email: string;
}

interface Notificacao {
  id: number;
  mensagem: string;
  data_cadastro: string;
  lido: boolean;
  id_usuario_destino: number | null;
  destino_nome: string;
  remetente_nome: string;
  data_leitura: string | null;
}

const Configuracoes: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const somenteMinhas = searchParams.get('somenteMinhas') === '1';
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [notificacoes, setNotificacoes] = useState<Notificacao[]>([]);
  const [minhasNotificacoes, setMinhasNotificacoes] = useState<Notificacao[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Estados do formulário
  const [mensagem, setMensagem] = useState('');
  const [usuariosSelecionados, setUsuariosSelecionados] = useState<number[]>([]);
  const [enviarParaTodos, setEnviarParaTodos] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    carregarDados();
  }, [somenteMinhas]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      setError('');
      console.log(' [CONFIGURACOES] Iniciando carregamento de dados...');
      
      // Carregar usuários
      console.log(' [CONFIGURACOES] Carregando usuários...');
      const usuariosResponse = await usuarioService.listar();
      console.log(' [CONFIGURACOES] Resposta usuários:', usuariosResponse);
      const usuariosData = usuariosResponse.data?.data || [];
      console.log(' [CONFIGURACOES] Usuários carregados:', usuariosData.length);
      setUsuarios(usuariosData);
      
      // Carregar notificações do usuário logado
      if (user?.id) {
        console.log(' [CONFIGURACOES] Carregando notificações do usuário:', user.id);
        const minhasNotificacoesResponse = await notificacaoService.listarPorUsuario(user.id);
        console.log(' [CONFIGURACOES] Resposta minhas notificações:', minhasNotificacoesResponse);
        const minhasNotificacoesData = minhasNotificacoesResponse.data?.data || [];
        console.log(' [CONFIGURACOES] Minhas notificações carregadas:', minhasNotificacoesData.length);
        setMinhasNotificacoes(minhasNotificacoesData);
      }
      
      // Carregar notificações (todas) somente quando não filtrando por minhas
      if (!somenteMinhas) {
        console.log(' [CONFIGURACOES] Carregando todas as notificações...');
        const notificacoesResponse = await notificacaoService.listar();
        console.log(' [CONFIGURACOES] Resposta todas notificações:', notificacoesResponse);
        const notificacoesData = notificacoesResponse.data?.data || [];
        console.log(' [CONFIGURACOES] Todas notificações carregadas:', notificacoesData.length);
        setNotificacoes(notificacoesData);
      } else {
        setNotificacoes([]);
      }
      
      console.log(' [CONFIGURACOES] Carregamento concluído com sucesso!');
    } catch (error) {
      console.error('❌ [CONFIGURACOES] Erro ao carregar dados:', error);
      const errorObj = error as any;
      console.error('❌ [CONFIGURACOES] Detalhes do erro:', errorObj.response?.data || errorObj.message);
      setError(`Erro ao carregar dados: ${errorObj.response?.data?.message || errorObj.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!mensagem.trim()) {
      setError('Mensagem é obrigatória');
      return;
    }
    
    if (!enviarParaTodos && usuariosSelecionados.length === 0) {
      setError('Selecione pelo menos um usuário ou marque "Enviar para todos"');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      if (enviarParaTodos) {
        // Enviar para todos
        await notificacaoService.criar({
          mensagem,
          enviar_para_todos: true
        });
        setSuccess('Notificação enviada para todos os usuários!');
      } else {
        // Enviar para usuários específicos
        for (const usuarioId of usuariosSelecionados) {
          await notificacaoService.criar({
            mensagem,
            id_usuario_destino: usuarioId,
            enviar_para_todos: false
          });
        }
        setSuccess(`Notificação enviada para ${usuariosSelecionados.length} usuário(s)!`);
      }
      
      // Limpar formulário
      setMensagem('');
      setUsuariosSelecionados([]);
      setEnviarParaTodos(false);
      setDialogOpen(false);
      
      // Recarregar notificações
      carregarDados();
      
    } catch (error) {
      console.error('Erro ao enviar notificação:', error);
      const errorObj = error as any;
      setError(`Erro ao enviar notificação: ${errorObj.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUsuarioChange = (usuarioId: number) => {
    setUsuariosSelecionados(prev => {
      if (prev.includes(usuarioId)) {
        return prev.filter(id => id !== usuarioId);
      } else {
        return [...prev, usuarioId];
      }
    });
  };

  const handleEnviarParaTodosChange = (checked: boolean) => {
    setEnviarParaTodos(checked);
    if (checked) {
      setUsuariosSelecionados([]);
    }
  };

  const excluirNotificacao = async (id: number) => {
    try {
      await notificacaoService.excluir(id);
      setSuccess('Notificação excluída com sucesso!');
      carregarDados();
    } catch (error) {
      console.error('Erro ao excluir notificação:', error);
      const errorObj = error as any;
      setError(`Erro ao excluir notificação: ${errorObj.message}`);
    }
  };

  const marcarNotificacaoComoLida = async (id: number) => {
    try {
      await notificacaoService.marcarComoLida(id);
      setMinhasNotificacoes(prev => prev.map(notificacao => notificacao.id === id ? { ...notificacao, lido: true, data_leitura: new Date().toISOString() } : notificacao));
      setSuccess('Notificação marcada como lida!');
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
      const errorObj = error as any;
      setError(`Erro ao marcar notificação como lida: ${errorObj.message}`);
    }
  };

  const totalNotificacoesChip = somenteMinhas ? minhasNotificacoes.length : notificacoes.length;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
        Configurações - Notificações
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Card para criar nova notificação */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Notifications sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Nova Notificação</Typography>
              </Box>
              
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setDialogOpen(true)}
                fullWidth
                sx={{ mt: 2 }}
              >
                Criar Notificação
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Card com estatísticas */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Estatísticas
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip 
                  icon={<Notifications />} 
                  label={`${totalNotificacoesChip} Total`} 
                  color="primary" 
                />
                <Chip 
                  icon={<People />} 
                  label={`${usuarios.length} Usuários`} 
                  color="secondary" 
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Lista de notificações */}
        {!somenteMinhas && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Notificações Enviadas
              </Typography>
              
              {notificacoes.length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  Nenhuma notificação encontrada
                </Typography>
              ) : (
                <List>
                  {notificacoes.map((notificacao) => (
                    <ListItem key={notificacao.id} divider>
                      <ListItemText
                        primary={notificacao.mensagem}
                        secondary={
                          <Box>
                            <Typography variant="caption" display="block">
                              Para: {notificacao.id_usuario_destino ? notificacao.destino_nome : 'Todos os usuários'}
                            </Typography>
                            <Typography variant="caption" display="block">
                              De: {notificacao.remetente_nome}
                            </Typography>
                            <Typography variant="caption" display="block">
                              Data: {new Date(notificacao.data_cadastro).toLocaleString('pt-BR')}
                            </Typography>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton 
                          edge="end" 
                          onClick={() => excluirNotificacao(notificacao.id)}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
        )}

        {/* Lista de minhas notificações */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Minhas Notificações
              </Typography>
              
              {minhasNotificacoes.length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  Nenhuma notificação encontrada
                </Typography>
              ) : (
                <List>
                  {minhasNotificacoes.map((notificacao) => (
                    <ListItem 
                      key={notificacao.id} 
                      divider
                      sx={{ 
                        bgcolor: notificacao.lido ? 'transparent' : 'action.hover',
                        borderLeft: notificacao.lido ? 'none' : '4px solid',
                        borderLeftColor: 'primary.main'
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: notificacao.lido ? 400 : 600 }}>
                              {notificacao.mensagem}
                            </Typography>
                            {!notificacao.lido && (
                              <Chip label="Nova" size="small" color="primary" />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="caption" display="block">
                              De: {notificacao.remetente_nome}
                            </Typography>
                            <Typography variant="caption" display="block">
                              Data: {new Date(notificacao.data_cadastro).toLocaleString('pt-BR')}
                            </Typography>
                            {notificacao.lido && notificacao.data_leitura && (
                              <Typography variant="caption" display="block" color="success.main">
                                Lida em: {new Date(notificacao.data_leitura).toLocaleString('pt-BR')}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {!notificacao.lido && (
                            <IconButton 
                              edge="end" 
                              onClick={() => marcarNotificacaoComoLida(notificacao.id)}
                              color="primary"
                              title="Marcar como lida"
                            >
                              <CheckCircle />
                            </IconButton>
                          )}
                          <IconButton 
                            edge="end" 
                            onClick={() => excluirNotificacao(notificacao.id)}
                            color="error"
                            title="Excluir notificação"
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog para criar notificação */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Send sx={{ mr: 1 }} />
              Criar Nova Notificação
            </Box>
          </DialogTitle>
          
          <DialogContent>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Mensagem"
              value={mensagem}
              onChange={(e) => setMensagem(e.target.value)}
              required
              sx={{ mb: 3, mt: 1 }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={enviarParaTodos}
                  onChange={(e) => handleEnviarParaTodosChange(e.target.checked)}
                />
              }
              label="Enviar para todos os usuários"
              sx={{ mb: 2 }}
            />

            {!enviarParaTodos && (
              <Paper sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Selecionar usuários:
                </Typography>
                {usuarios.map((usuario) => (
                  <FormControlLabel
                    key={usuario.id}
                    control={
                      <Checkbox
                        checked={usuariosSelecionados.includes(usuario.id)}
                        onChange={() => handleUsuarioChange(usuario.id)}
                      />
                    }
                    label={`${usuario.nome || usuario.user_name} (${usuario.email})`}
                    sx={{ display: 'block' }}
                  />
                ))}
              </Paper>
            )}
          </DialogContent>
          
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={loading}
              startIcon={<Send />}
            >
              {loading ? 'Enviando...' : 'Enviar Notificação'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Configuracoes;
